import React, { useState, useEffect, useRef } from 'react';
import Routes from '../../routes';
import { withRouter } from 'react-router-dom';
import Joyride from 'react-joyride';
import DemoToolTip from '../Common/DemoToolTip';
import AccountMenu from '../AccountMenu';
import Search from '../Search';
import { toolTipBlue, trakrefYellow, red, Grey2, Grey1, white, White, darkTeal } from '../../color_utils';
import { Button, IconButton, Typography, Drawer, AppBar, Toolbar, Grid, Tooltip, Alert, Collapse, Paper, Box } from '@mui/material';
import { trakrefFontFamily } from '../../utils';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import ForumIcon from '@mui/icons-material/ForumOutlined';
import ReleaseNotesIcon from '../../Icons/ic_Release-Notes.svg';
import useNavBar from '../../context/NavBar/useNavBar';
import useSearch from '../../context/Search/SearchContext';
import { useIsDesktop } from '../../hooks';
import './NavMenu.css';
import NotificationDisplay from '../Notification';
import { Translate } from '../../Utils/Languages/Translate';
import { SearchIcon } from '../../Icons/Icons';
import MenuIcon from '@mui/icons-material/Menu';
import SideNav from '../SideNav';

const NavBar = ({
    logout,
    history: {
        push = () => { }
    },
    showDemoVideo,
    renderAlert,
    alertText,
    alertType,
    setAlert,
    customAlertStyle,
    isOnline,
}) => {
    const releaseNotesURL = "https://trakrefv4.zendesk.com/hc/en-us/categories/23789686430605-Release-Notes";
    const toolBarStyle = { height: { xs: '8vh', md: '14vh', lg: "14vh" }, paddingRight: { xs: '0 !important', md: '10px !important', lg: 0 }, pt: { md: 2 } };
    const appBarStyle = { zIndex: 10, backgroundColor: '#00859b', position: 'relative', padding: '0px !important' };
    const genericAlertStyle = { color: '#FFF', fontFamily: trakrefFontFamily, '& .MuiAlert-icon': { color: '#FFF' } };
    const isDesktop = useIsDesktop();
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const alertStyles = {
        success: {
            backgroundColor: '#8BDC54',
            ...genericAlertStyle
        },
        warning: {
            backgroundColor: trakrefYellow,
            ...genericAlertStyle
        },
        error: {
            backgroundColor: red,
            ...genericAlertStyle
        }
    };
    const iconColor = { color: '#fff' };
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: 48 * 4.5 + 8,
                maxWidth: 420,
            },
            sx: {
                '& .MuiMenuItem-root': {
                    color: "#000",
                    fontFamily: trakrefFontFamily,
                    fontSize: "12px",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "23px",
                    letterSpacing: "0.88px",
                },
            },
        }
    };
    const demoSteps = [
        {
            target: '.MainSearchBar',
            content1: 'Search for anything - anywhere. 🔎',
            content2: 'Use the search box to find work orders, service events, locations and inventory.',
            placement: "right-end",
            disableBeacon: true
        },
        {
            target: '.sideNavDemo',
            content1: 'You asked for simpler, cleaner navigation and we listened!',
            content2: 'Now you can easily navigate to your work orders, service events, inventory, locations & reports. 🧭',
            placement: "right",
            disableBeacon: true
        },
        /* {
            target: '.trakrefClassicDemo',
            content1: `The new Trakref still doesn't have all the features available.`,
            content2: 'To find all the features, log into Trakref Classic.',
            placement: "left",
            disableBeacon: true
        }, */
    ];
    const { instances, userData, getData, changeInstance } = useNavBar();
    const { dispatch } = useSearch();

    const [demo, setDemo] = useState(false);
    const [drawer, setDrawer] = useState(false);
    const [openNotification, setOpenNotification] = useState(false);
    const [newNotification, setNewNotification] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [showSearch, setShowSearch] = useState(false);
    const disabled = !isOnline;
    const closeAlert = () => setAlert({ render: false });

    const closeDrawer = () => setDrawer(false);

    const handleSearchFocus = () => {
        // Close drawers if open
        const shouldRefocus = mobileDrawerOpen || drawer;
        if (mobileDrawerOpen) setMobileDrawerOpen(false);
        if (drawer) setDrawer(false);

        // If drawer was open, ensure input stays focused after closing
        if (shouldRefocus && searchRef.current) {
            setTimeout(() => {
                if (searchRef.current && searchRef.current.focus) {
                    searchRef.current.focus();
                }
            }, 100);
        }
    };

    const toggleNotification = () => {
        setOpenNotification(!openNotification);

        if (newNotification) {
            fetch(Routes.Notification.UpdateLastSeen(), {
                method: 'PATCH',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });
            setNewNotification(false);
        }

    };

    const notificationRef = useRef(null);

    useEffect(() => {
        getData();
        const fetchData = async () => {
            const response = await fetch(Routes.Notification.Get());
            const data = await response.json();
            if (data && data.length > 0) {
                setNewNotification(true);
            }
            setNotifications(data);
        };
        fetchData();
    }, []);

    useEffect(() => {
        const dismissNotificationBox = (event) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setOpenNotification(false);
            }
        };

        if (openNotification) {
            document.addEventListener('click', dismissNotificationBox);
        } else {
            document.removeEventListener('click', dismissNotificationBox);
        }

        return () => {
            document.removeEventListener('click', dismissNotificationBox);
        };
    }, [openNotification]);
    
    useEffect(() => {
        if (isOnline && renderAlert) {
            setAlert({
                render: true,
                text: 'You are back online!',
                type: 'success'
            });
            const closeAlertTimer = setTimeout(closeAlert, 7500);
            return () => clearTimeout(closeAlertTimer);
        }

        if (!isOnline) {
            setAlert({
                render: true,
                text: 'You’re offline. Please reconnect to continue working.',
                type: 'error'
            });
        }
    }, [window.navigator.onLine]);

    useEffect(() => {
        if (mobileDrawerOpen || drawer) {
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
        }
    
        return () => {
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
        };
    }, [mobileDrawerOpen, drawer]);

    // If the viewport grows into desktop while the mobile drawer is open, close
    // it so its body scroll-lock (above) is released and the hidden drawer
    // doesn't leave the page frozen.
    useEffect(() => {
        if (isDesktop && mobileDrawerOpen) {
            setMobileDrawerOpen(false);
        }
    }, [isDesktop, mobileDrawerOpen]);

    const searchRef = useRef(null);

    const handleSearchBlur = () => {
        if (!isDesktop) {
            setShowSearch(false);
            // Clear the search input but preserve lastQuery for results page
            dispatch({ type: "SET_QUERY", payload: "" });
        }
    };

    return (
        <>
            <Joyride
                continuous
                start={true}
                disableOverlayClose
                run={demo}
                disableScrolling={true}
                steps={demoSteps}
                tooltipComponent={(props) => <DemoToolTip {...props} setDemo={() => setDemo(false)} showDemoVideo={showDemoVideo} />}
                styles={{
                    options: {
                        arrowColor: toolTipBlue,
                        primaryColor: toolTipBlue,
                    },
                }}
                floaterProps={{ hideArrow: true }}
            />

            <AppBar position="fixed" sx={appBarStyle} elevation={0}>
                <Toolbar sx={toolBarStyle}>
                    <Grid item sx={{ display: isDesktop ? 'none' : 'block', marginRight: { xs: showSearch ? '10px' : 0, md: '10px' }}}>
                        <IconButton
                            disabled={disabled}
                            onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}
                            aria-label="menu"
                            sx={{
                                border: `1px solid ${White}`,
                                borderRadius: '4px',
                                p: '0px',
                            }}
                        >
                            <MenuIcon sx={{ color: White }} />
                        </IconButton>
                    </Grid>
                    <Grid container direction='row' alignItems="center" justifyContent={'space-between'}>
                        <Grid container item xs={2} lg={2} justifyContent="center" sx={{ display: { xs: 'none', md: 'block'}, pl: {lg: 6} }}>
                            <img
                                src={'/images/whitelogo_2025.svg'}
                                alt="trakref"
                                className="nav-logo"
                                onClick={() => { if (!disabled) push('/'); }}
                                style={disabled ? { cursor: 'default', opacity: 0.6 } : {}}
                            />
                        </Grid>
                        <Grid
                            item
                            md={4}
                            xs={'auto'}
                            lg={6}
                            sx={{
                                display: { xs: (showSearch ? 'block' : 'none'), md: 'block' },
                                flex: { xs: 1, md: 'full', },
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Search
                                push={push}
                                inputRef={searchRef}
                                onBlur={handleSearchBlur}
                                onSearchFocus={handleSearchFocus}
                                disabled={disabled}
                            />
                        </Grid>
                        {!showSearch &&
                            <Grid item xs={1} sx={{ display: { xs: 'block', md: 'none' }, marginLeft: '24px', }}>
                                <IconButton
                                    disabled={disabled}
                                    onClick={() => {
                                        if (mobileDrawerOpen) setMobileDrawerOpen(false);
                                        if (drawer) setDrawer(false);
                                        setShowSearch(true);
                                        setTimeout(() => {
                                            if (searchRef.current) {
                                                searchRef.current.focus();
                                            }
                                        }, 100);
                                    }}
                                >
                                    <SearchIcon width="16px" height="16px" color={White} />
                                </IconButton>
                            </Grid>
                        }
                        <Grid container item xs="auto" alignItems="center" justifyContent="flex-end" sx={{ flexWrap: 'nowrap', flexDirection: 'column' }}>
                            <Grid container item alignItems="center" justifyContent="flex-end" sx={{ flexWrap: 'nowrap', gap: { xs: 0, lg: 2} } }>
                                <Grid item sx={{ marginLeft: 'auto', display: { xs: 'none', md: 'inline-flex' } }}>
                                    <Typography sx={{ fontFamily: trakrefFontFamily, whiteSpace: 'nowrap' }}>
                                        Hi {userData.firstName}
                                    </Typography>
                                </Grid>
                                {!showSearch &&
                                    <Grid item sx={{ marginLeft: 'auto', display: { xs: 'inline-flex', md: 'none' } }}>
                                        <Typography sx={{ fontFamily: trakrefFontFamily, fontSize: '12px', whiteSpace: 'nowrap' }}>
                                            {userData.instance &&
                                                instances?.find(i => i.id === userData.instance)?.name
                                            }
                                        </Typography>
                                    </Grid>
                                }
                                <Grid item sx={{ display: { xs: 'none', md: 'block' } }}>
                                    <div className='navbar-notification' ref={notificationRef}>
                                        <div className='navbar-notification-icon'>
                                            <IconButton
                                                aria-label="close"
                                                onClick={() => toggleNotification()}
                                                disabled={disabled}
                                             >
                                                <NotificationsOutlinedIcon sx={iconColor} />
                                            </IconButton>
                                            {newNotification && <div className="navbar-notification-count" />}
                                        </div>
                                        <NotificationDisplay
                                            isShowing={openNotification}
                                            notifications={notifications}
                                            onClose={
                                                () => setOpenNotification(false)
                                            }
                                        />
                                    </div>
                                </Grid>
                                <Grid item sx={{ display: { xs: 'none', md: 'block' } }}>
                                    <Tooltip title={Translate("knowledge_base")}>
                                          <IconButton
                                              disabled={disabled}
                                              onClick={() => window.open('https://trakrefv4.zendesk.com/hc/en-us', '_blank')}
                                          >
                                            <HelpOutlineIcon sx={iconColor} />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                                <Grid item sx={{ display: { xs: 'none', md: 'block' } }}>
                                    <Tooltip title="What's New">
                                         <IconButton
                                             disabled={disabled}
                                             onClick={() => window.open(releaseNotesURL, '_blank')}
                                         >
                                            <img src={ReleaseNotesIcon} alt="Release Notes" />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                                {!showSearch &&
                                    <Grid item sx={{ marginLeft: { xs: "4%", md: '2%' } }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<ForumIcon />}
                                            disabled={disabled}
                                            onClick={() => {
                                                if (window.zE) {
                                                    window.zE('webWidget', 'show');
                                                    window.zE('webWidget', 'open');
                                                }
                                            }}
                                            sx={{
                                                backgroundColor: darkTeal,
                                                borderRadius: {xs: '10px', md: '100px' },
                                                textTransform: 'none',
                                                padding: { xs: '6px 8px', md: '6px 16px' },
                                                minWidth: { xs: 'auto', md: undefined },
                                                '&:hover': {
                                                    backgroundColor: darkTeal,
                                                },
                                                '& .MuiButton-startIcon': {
                                                    margin: { xs: 0, md: '0 6px 0 0' }
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: { xs: 'none', md: 'inline' }, fontSize: '12px'  }}>Support</Box>
                                        </Button>
                                    </Grid>
                                }
                                <Grid item sx={{ marginLeft: { xs: "1.5%", md: '1%' } }}>
                                    <IconButton
                                        disabled={disabled}
                                        onClick={() => setDrawer(!drawer)}
                                    >
                                        <MoreVertIcon sx={iconColor} />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container item xs={12} lg={12} justifyContent="flex-end" sx={{ marginLeft: 'auto', mb: 1, display: { xs: 'none', md: 'inline-flex' } }}>
                            <Typography
                                mr={"13px"}
                                sx={{
                                    fontFamily: trakrefFontFamily,
                                    fontSize: '14px',
                                    marginTop: 1,
                                }}
                            >
                                <span>{(userData.instance && instances.find(instance => instance.id === userData.instance)?.name) ?? ""}</span>
                            </Typography>
                        </Grid>
                    </Grid>
                </Toolbar>
            </AppBar>

            <Collapse in={renderAlert} timeout="auto" unmountOnExit sx={{ marginLeft: isDesktop ? '12vw' : 0 }}>
                <Paper elevation={3}>
                    <Alert
                        severity={alertType}
                        sx={{ ...alertStyles[alertType], ...customAlertStyle }}
                        iconMapping={{
                            success: <CheckCircleIcon fontSize="inherit" />,
                            error: <ErrorIcon fontSize='inherit' />,
                        }}
                        // onClose={() => closeAlert()}
                    >
                        {alertText}
                    </Alert>
                </Paper>
            </Collapse>

            <Drawer
                anchor='right'
                open={drawer}
                disableEnforceFocus
                onClose={() => setDrawer(false)}
                disableScrollLock={false}
                sx={{
                    zIndex: 1,
                    '& .MuiDrawer-paper': {
                        paddingTop: { xs: '80px', md: '12vh', lg: '14vh' },
                    }
                }}
            >
                <AccountMenu logout={logout} userData={userData} closeDrawer={closeDrawer} instances={instances} />
            </Drawer>

            <Drawer
                anchor='left'
                open={mobileDrawerOpen}
                onClose={() => setMobileDrawerOpen(false)}
                sx={{
                    display: isDesktop ? 'none' : 'block',
                    zIndex: 1,
                    '& .MuiDrawer-paper': {
                        width: '70vw',
                    }
                }}
            >
                <SideNav closeMobileDrawer={() => setMobileDrawerOpen(false)} />
            </Drawer>

        </>
    );
};

export default withRouter(NavBar);
